/* src/contexts/profile/profileActionsJournal.js */
import { useCallback } from 'react';
import { collection, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db as firestoreDb } from '../../firebaseConfig';
import { sanitizeTag } from './profileUtils';

/**
 * Hook creator for Add Journal Tag action.
 * NOTE: Relies on updateProfileData action, which modifies local state.
 */
export const useCreateAddJournalTagAction = (
    authUserRef,
    profiles, // Read-only state needed
    updateProfileData, // Action needed
    setError
) => {
    return useCallback(async (profileId, rawTag) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId) {
            setError("Auth/profile ID missing for adding tag.");
            return false;
        }

        const tag = sanitizeTag(rawTag);
        if (!tag) {
            return false; // Invalid format
        }

        const profile = profiles.find(p => p.id === profileId);
        if (!profile) {
            setError("Profile not found to add tag.");
            return false;
        }

        const currentTags = Array.isArray(profile.journalTags) ? profile.journalTags : [];
        if (currentTags.includes(tag)) {
            return tag; // Tag already exists
        }

        const newTags = [...currentTags, tag].sort();
        // Use updateProfileData to handle Firestore update and local state update
        const success = await updateProfileData(profileId, { journalTags: newTags });

        if (success) {
            console.log(`[addJournalTag] Successfully added tag "${tag}" to profile ${profileId}.`);
            return tag;
        } else {
            console.error(`[addJournalTag] Failed to add tag "${tag}" using updateProfileData.`);
            // Error likely set by updateProfileData
            return false;
        }
    }, [authUserRef, profiles, updateProfileData, setError]);
};

/**
 * Hook creator for Add Journal Entry action.
 * NOTE: May call updateProfileData if new tags are added.
 */
export const useCreateAddJournalEntryAction = (
    authUserRef,
    profiles, // Read-only state needed for tags
    updateProfileData, // Action potentially needed for tags
    setError
) => {
    return useCallback(async (profileId, entryData) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || !entryData) {
            setError("Missing user ID, profile ID, or entry data for adding journal entry.");
            return null;
        }
        setError(null);

        const cleanedTags = Array.isArray(entryData.tags)
            ? [...new Set(entryData.tags.map(sanitizeTag).filter(Boolean))].sort()
            : [];

        const payload = {
             ...entryData,
             tags: cleanedTags,
             userId: currentUserId,
             profileId: profileId,
             timestamp: entryData.timestamp instanceof Date ? Timestamp.fromDate(entryData.timestamp) : serverTimestamp(),
             createdAt: serverTimestamp(),
             updatedAt: serverTimestamp()
        };
        delete payload.id;

        const db = firestoreDb;
        try {
             const journalColRef = collection(db, 'users', currentUserId, 'profiles', profileId, 'journal');
             const docRef = await addDoc(journalColRef, payload);
             console.log(`[addJournalEntry] Successfully added journal entry ${docRef.id} for profile ${profileId}.`);

             // Update Profile's Global Tags if needed
             const profile = profiles.find(p => p.id === profileId);
             const existingTags = profile?.journalTags || [];
             const tagsToAdd = cleanedTags.filter(t => !existingTags.includes(t));

             if (tagsToAdd.length > 0) {
                 console.log(`[addJournalEntry] Adding new tags to profile: ${tagsToAdd.join(', ')}`);
                 // Call updateProfileData (which handles local state too)
                 await updateProfileData(profileId, { journalTags: [...existingTags, ...tagsToAdd] });
             }

             // Listener will handle adding the entry to local state (activeJournal)
             // Return data with approximate timestamps for immediate use if needed by caller
             const returnedData = {
                ...payload,
                id: docRef.id,
                timestamp: payload.timestamp instanceof Timestamp ? payload.timestamp.toDate() : new Date(), // Approx
                createdAt: new Date(), // Approx
                updatedAt: new Date(), // Approx
             };
             return returnedData;

        } catch (err) {
             console.error("[addJournalEntry] Error adding journal entry to Firestore:", err);
             setError("Failed to save journal entry. Please try again.");
             return null;
        }
    }, [authUserRef, profiles, updateProfileData, setError]);
};

/**
 * Hook creator for Update Journal Entry action.
 * NOTE: May call updateProfileData if new tags are added.
 */
export const useCreateUpdateJournalEntryAction = (
    authUserRef,
    profiles, // Read-only state needed for tags
    updateProfileData, // Action potentially needed for tags
    setError
) => {
    return useCallback(async (profileId, entryId, updatedData) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || !entryId || !updatedData) {
            setError("Missing IDs or update data for journal entry.");
            return false;
        }
        setError(null);

        const payload = { updatedAt: serverTimestamp() };
        let cleanedTags = undefined;

        if (updatedData.content !== undefined && typeof updatedData.content === 'string') {
            payload.content = updatedData.content;
        }
        if (updatedData.tags !== undefined && Array.isArray(updatedData.tags)) {
            cleanedTags = [...new Set(updatedData.tags.map(sanitizeTag).filter(Boolean))].sort();
            payload.tags = cleanedTags;
        }
        // Add other editable fields if necessary

        if (Object.keys(payload).length <= 1) {
            console.warn("[updateJournalEntry] No valid fields provided for update.");
            return true;
        }

        const db = firestoreDb;
        try {
             const entryRef = doc(db, 'users', currentUserId, 'profiles', profileId, 'journal', entryId);
             await updateDoc(entryRef, payload);
             console.log(`[updateJournalEntry] Successfully updated journal entry ${entryId}.`);

             // Update Profile's Global Tags if tags were changed and new ones added
             if (cleanedTags) {
                 const profile = profiles.find(p => p.id === profileId);
                 const existingTags = profile?.journalTags || [];
                 const tagsToAdd = cleanedTags.filter(t => !existingTags.includes(t));
                 if (tagsToAdd.length > 0) {
                      console.log(`[updateJournalEntry] Adding new tags to profile from update: ${tagsToAdd.join(', ')}`);
                      // Call updateProfileData
                      await updateProfileData(profileId, { journalTags: [...existingTags, ...tagsToAdd] });
                 }
             }
             // Listener will handle updating the entry in local state (activeJournal)
             return true;

        } catch (err) {
             console.error(`[updateJournalEntry] Error updating journal entry ${entryId}:`, err);
             setError("Failed to update journal entry.");
             return false;
        }
    }, [authUserRef, profiles, updateProfileData, setError]);
};

/**
 * Hook creator for Remove Journal Entry action.
 */
export const useCreateRemoveJournalEntryAction = (
    authUserRef,
    setError
) => {
    return useCallback(async (profileId, entryId) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || !entryId) {
            setError("Missing IDs for journal entry removal.");
            return false;
        }
        setError(null);
        const db = firestoreDb;
        try {
             const entryRef = doc(db, 'users', currentUserId, 'profiles', profileId, 'journal', entryId);
             await deleteDoc(entryRef);
             console.log(`[removeJournalEntry] Successfully removed journal entry ${entryId}.`);
             // Listener will handle removing the entry from local state (activeJournal)
             return true;
        } catch (err) {
             console.error(`[removeJournalEntry] Error removing journal entry ${entryId}:`, err);
             setError("Failed to remove journal entry.");
             return false;
        }
    }, [authUserRef, setError]);
};